import { computed, makeObservable, observable, action } from 'mobx';
import { getRollingLimits, putRollingLimits } from 'src_server/trpc/types/responsibleGambling';
import { RollingNetDepositLimitItemState } from 'src/domains/players/webview/components/SignUp/signupPrimary/signUpViews/responsibleGambling/rollingNetDepositLimit/RollingNetDepositLimitItem';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import {
    buildRollingNetDepositLimitData,
    ResourceCustomer,
    RollingNetDepositLimitDataType,
    rollingLimitCombinationKey,
} from 'src/domains/players/state/UsersState';
import { Amount } from 'src_common/common/amount/Amount';
import {
    RollingPayloadListType,
    preparePayload,
} from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/DepositLimitsProcedure.state';
import {
    buildDepositLimitConfigMap,
    DepositLimitConfigMapType,
    resolveSignupLimitWindowType,
} from 'src/domains/players/webview/components/Account/limitsTab/rollingNetDepositLimit/depositLimitType';
import { CreateAccountStep } from 'src/domains/players/webview/components/SignUp';
import { CurrencyType, isCurrencyCode } from 'src_common/common/amount/website-money/currency';
import { Common } from 'src/domains/common/Common';

export type InfoMessageType = 'success' | 'error' | 'empty' | 'belowOne' | 'amountTypeMismatch';

export type SignupRollingDepositLimitWindowDaysType = 1 | 7 | 30;
export type SignupDepositLimitsType = 'calendar' | 'rolling';

interface ConvertedLimitsType {
    [key: string]: Amount | undefined;
}

export class SignupRollingNetDepositLimitState {
    public readonly daily: RollingNetDepositLimitItemState;
    public readonly weekly: RollingNetDepositLimitItemState;
    public readonly monthly: RollingNetDepositLimitItemState;

    private readonly rollingNetDepositLimitData: ResourceCustomer<RollingNetDepositLimitDataType>;
    private readonly depositLimitConfigData: ResourceCustomer<DepositLimitConfigMapType>;

    @observable public infoMessage: InfoMessageType | null = null;

    /** Per-window gross/net choice (UI); each row carries calculationType in putRollingLimits.list. */
    @observable private calculationTypeUserByWindow: Partial<
        Record<SignupRollingDepositLimitWindowDaysType, getRollingLimits.CalculationTypeSchemaType>
    > = {};

    public constructor(
        private readonly common: Common,
        private readonly createAccountStep: CreateAccountStep
    ) {
        makeObservable(this);

        this.rollingNetDepositLimitData = new ResourceCustomer(
            'rollingNetDepositLimitData',
            common.session,
            async () => {
                const payload = await this.common.trpcClient.client.responsibleGamblingRouter.getRollingLimits.query();

                if (payload.responseStatus === 'success') {
                    return buildRollingNetDepositLimitData(payload.data.limits);
                }
                return buildRollingNetDepositLimitData([]);
            },
            { allowEphemeral: true }
        );
        this.depositLimitConfigData = new ResourceCustomer(
            'depositLimitConfigData',
            common.session,
            async () => {
                const payload =
                    await this.common.trpcClient.client.responsibleGamblingRouter.getDepositLimitConfig.query();
                if (payload.responseStatus === 'success') {
                    return buildDepositLimitConfigMap(payload.data);
                }
                return buildDepositLimitConfigMap([]);
            },
            { allowEphemeral: true }
        );

        this.daily = new RollingNetDepositLimitItemState(
            ConfigComponents.get(this.common),
            this.selectedCurrency,
            this.clearInfoMessage,
            () => this.rollingLimitsByWindow.get(1),
            () => this.signupMaxLimitPence(1)
        );

        this.weekly = new RollingNetDepositLimitItemState(
            ConfigComponents.get(this.common),
            this.selectedCurrency,
            this.clearInfoMessage,
            () => this.rollingLimitsByWindow.get(7),
            () => this.signupMaxLimitPence(7)
        );

        this.monthly = new RollingNetDepositLimitItemState(
            ConfigComponents.get(this.common),
            this.selectedCurrency,
            this.clearInfoMessage,
            () => this.rollingLimitsByWindow.get(30),
            () => this.signupMaxLimitPence(30)
        );
    }

    @computed public get rollingLimitsByWindow(): Map<number, getRollingLimits.LimitsObjectSchemaType> {
        const data = this.rollingNetDepositLimitData.valueReady;
        if (data === null) {
            return new Map();
        }
        return data.byWindow;
    }

    public limitTypeForWindow(days: SignupRollingDepositLimitWindowDaysType): SignupDepositLimitsType {
        return resolveSignupLimitWindowType(
            days,
            this.calculationTypeUserByWindow[days],
            this.depositLimitConfigData.valueReady ?? undefined
        );
    }

    /**
     * Max is getRollingLimits.limitValueMax only when that response contains
     * this window + chosen gross/net + calendar/rolling. Otherwise there is no max.
     */
    public signupMaxLimitPence(days: SignupRollingDepositLimitWindowDaysType): number | undefined {
        const calculationType = this.calculationTypeUserByWindow[days];
        if (calculationType === undefined) {
            return undefined;
        }

        const limitValueMax = this.rollingLimitFor(days, calculationType, this.limitTypeForWindow(days))?.limitValueMax;
        if (limitValueMax === null || limitValueMax === undefined) {
            return undefined;
        }

        return limitValueMax;
    }

    private rollingLimitFor(
        days: SignupRollingDepositLimitWindowDaysType,
        calculationType: getRollingLimits.CalculationTypeSchemaType,
        limitType: SignupDepositLimitsType
    ): getRollingLimits.LimitsObjectSchemaType | undefined {
        const data = this.rollingNetDepositLimitData.valueReady;
        if (data === null) {
            return undefined;
        }

        return data.byWindowCalculationAndLimitType.get(rollingLimitCombinationKey(days, calculationType, limitType));
    }

    public calculationTypeForWindow(
        days: SignupRollingDepositLimitWindowDaysType
    ): getRollingLimits.CalculationTypeSchemaType | undefined {
        return this.calculationTypeUserByWindow[days];
    }

    @action public setCalculationTypeForWindow = (
        days: SignupRollingDepositLimitWindowDaysType,
        value: getRollingLimits.CalculationTypeSchemaType
    ): void => {
        const next = { ...this.calculationTypeUserByWindow };
        if (next[days] === value) {
            delete next[days];
        } else {
            next[days] = value;
        }
        this.calculationTypeUserByWindow = next;
        this.clearInfoMessage();
    };

    private clearInfoMessage = (): void => {
        this.infoMessage = null;
    };

    private clearInputs = (): void => {
        this.daily.resetValue();
        this.weekly.resetValue();
        this.monthly.resetValue();
    };

    @computed public get isLoading(): boolean {
        const depositsModel = this.rollingNetDepositLimitData.get();
        const configModel = this.depositLimitConfigData.get();

        return depositsModel.type === 'loading' || configModel.type === 'loading';
    }

    @computed public get isErrorInput(): boolean {
        return this.daily.isError || this.weekly.isError || this.monthly.isError;
    }

    private getActualLimits = (): ConvertedLimitsType => {
        const getValue = (period: RollingNetDepositLimitItemState): Amount | undefined => {
            const shortenValue = period.limitAmount.result.value;
            return shortenValue.type === 'ok' ? shortenValue.data : undefined;
        };

        return {
            dailyValue: getValue(this.daily),
            weeklyValue: getValue(this.weekly),
            monthlyValue: getValue(this.monthly),
        };
    };

    private typedAmountForWindow(days: SignupRollingDepositLimitWindowDaysType): Amount | undefined {
        const { dailyValue, weeklyValue, monthlyValue } = this.getActualLimits();
        if (days === 1) {
            return dailyValue;
        }
        if (days === 7) {
            return weeklyValue;
        }
        return monthlyValue;
    }

    /** A window is valid when the user fills both the amount and the type, or neither. */
    private hasAmountAndTypeMismatch(): boolean {
        const windows: SignupRollingDepositLimitWindowDaysType[] = [1, 7, 30];
        return windows.some((days) => {
            const hasAmount = this.typedAmountForWindow(days) !== undefined;
            const hasType = this.calculationTypeUserByWindow[days] !== undefined;
            return hasAmount !== hasType;
        });
    }

    private checkIsLessThanOne(limits: ConvertedLimitsType): boolean {
        const limit = new Amount('1');
        const values = [limits.dailyValue, limits.weeklyValue, limits.monthlyValue];

        return values.some((value) => value !== undefined && value.isLessThan(limit));
    }

    private async updateRollingLimits(
        payload: RollingPayloadListType,
        currentLimits: getRollingLimits.SuccessResponseType
    ): Promise<putRollingLimits.ResponseType> {
        const preparedPayload = preparePayload(payload, currentLimits);
        const response = await this.common.trpcClient.client.responsibleGamblingRouter.putRollingLimits.mutate({
            payload: {
                source: 'registration',
                list: preparedPayload,
            },
        });
        return response;
    }

    private setTriggersWarningEmail = (limitValueMax: number | null, pendingLimitValue: number): boolean => {
        return pendingLimitValue === limitValueMax ? true : false;
    };

    private async updateLimits(): Promise<boolean> {
        const daysArray = [1, 7, 30] as const;
        const preparedPayload: putRollingLimits.LimitObjectType[] = [];

        daysArray.forEach((days) => {
            const calculationType = this.calculationTypeUserByWindow[days];
            const amountToSend = this.typedAmountForWindow(days);
            if (amountToSend === undefined || calculationType === undefined) {
                return;
            }

            const previousLimitData = this.rollingLimitsByWindow.get(days);

            const pendingLimitValue = parseInt(amountToSend.multiply(100).value, 10);
            const limitType = resolveSignupLimitWindowType(
                days,
                calculationType,
                this.depositLimitConfigData.valueReady ?? undefined
            );
            const limitValueMax = this.rollingLimitFor(days, calculationType, limitType)?.limitValueMax ?? null;
            const triggersWarningEmail = this.setTriggersWarningEmail(limitValueMax, pendingLimitValue);

            preparedPayload.push({
                windowLengthInDays: days,
                pendingLimitValue,
                limitValueMax,
                triggersWarningEmail,
                hardLimit: previousLimitData?.hardLimit ?? false,
                limitType,
                calculationType,
            });
        });

        if (preparedPayload.length === 0) {
            return true;
        }

        const currentLimits = await this.common.trpcClient.client.responsibleGamblingRouter.getRollingLimits.query();

        if (currentLimits.responseStatus !== 'success') {
            this.infoMessage = 'error';
            return false;
        }

        const response = await this.updateRollingLimits(preparedPayload, currentLimits.data);

        this.infoMessage = response.responseStatus === 'success' ? 'success' : 'error';

        if (response.responseStatus === 'success') {
            this.rollingNetDepositLimitData.refresh();
        }

        return response.responseStatus === 'success';
    }

    public setAtLeastOneLimit = (): boolean => {
        const { dailyValue, weeklyValue, monthlyValue } = this.getActualLimits();

        if (dailyValue === undefined && weeklyValue === undefined && monthlyValue === undefined) {
            return false;
        }
        return true;
    };

    @action public onSubmit = async (): Promise<boolean> => {
        if (this.isErrorInput === true) {
            return true;
        }

        if (this.hasAmountAndTypeMismatch()) {
            this.infoMessage = 'amountTypeMismatch';
            return false;
        }

        const { dailyValue, weeklyValue, monthlyValue } = this.getActualLimits();
        const typedNothing = dailyValue === undefined && weeklyValue === undefined && monthlyValue === undefined;
        const hasServerDefault = ([1, 7, 30] as const).some((days) => {
            const activeLimitValue = this.rollingLimitsByWindow.get(days)?.activeLimitValue;
            return activeLimitValue !== undefined && activeLimitValue !== null;
        });

        if (typedNothing && hasServerDefault === false) {
            this.clearInputs();
            this.infoMessage = 'empty';
            return true;
        }

        if (this.checkIsLessThanOne({ dailyValue, weeklyValue, monthlyValue })) {
            this.clearInputs();
            this.infoMessage = 'belowOne';
            return true;
        }

        try {
            return await this.updateLimits();
        } catch (error) {
            console.log(error);
            this.infoMessage = 'error';
            return false;
        }
    };

    @computed public get selectedCurrency(): CurrencyType {
        const currency = this.createAccountStep.yourDetailsState.currency.currentValue;

        if (isCurrencyCode(currency)) {
            return currency;
        }

        return 'GBP';
    }
}
